import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, HandCoins, Users } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { ASSETS } from "@/lib/api";

export const Hero = () => {
  const { scrollY } = useScroll();
  const yBadge = useTransform(scrollY, [0, 600], [0, 120]);
  const yGlow = useTransform(scrollY, [0, 600], [0, -80]);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden grain sec-hero" data-testid="hero-section">
      <motion.div
        style={{ y: yGlow }}
        className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-yellow-400/10 blur-[140px]"
      />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-yellow-500/5 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-32 pb-20 grid lg:grid-cols-[1.4fr_1fr] gap-14 items-center w-full">
        <div>
          <h1 className="font-display font-black uppercase leading-[0.9] text-5xl sm:text-7xl lg:text-[6.5rem] tracking-tight">
            <MaskedLine delay={0.15} className="text-white">Revolutionary</MaskedLine>
            <MaskedLine delay={0.3} className="text-yellow-400">Goans</MaskedLine>
            <MaskedLine delay={0.45} className="text-white">Party</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 max-w-xl text-zinc-400 text-base md:text-lg leading-relaxed"
          >
            A grassroots movement of Goans, by Goans, for Goans. Fighting to
            protect Goan identity, land, livelihood and future.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button data-testid="hero-join-button" className="btn-yellow" onClick={() => go("membership")}>
              <Users size={18} /> Become a Revolutionary
            </button>
            <button data-testid="hero-donate-button" className="btn-outline" onClick={() => go("donate")}>
              <HandCoins size={18} /> Join the Revolution
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: yBadge }} className="hidden lg:flex justify-center relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-full bg-yellow-400/20 blur-3xl scale-110" />
            <div className="spin-slow absolute -inset-8">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                <defs>
                  <path id="circlePath" d="M 100,100 m -88,0 a 88,88 0 1,1 176,0 a 88,88 0 1,1 -176,0" />
                </defs>
                <text className="font-mono-rgp" fill="#FACC15" fontSize="10.5" letterSpacing="3">
                  <textPath href="#circlePath" textLength="552" lengthAdjust="spacing">
                    REVOLUTIONARY GOANS PARTY • REVOLUTIONARY GOANS PARTY •
                  </textPath>
                </text>
              </svg>
            </div>
            <img
              src={ASSETS.logo}
              alt="Revolutionary Goans Party official logo"
              className="relative w-72 h-72 rounded-full object-cover border-4 border-yellow-400/60 shadow-[0_0_80px_rgba(250,204,21,0.25)]"
            />
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => go("about")}
        data-testid="hero-scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-yellow-400/70 hover:text-yellow-400 transition-colors"
      >
        <ArrowDown className="animate-bounce" size={22} />
      </motion.button>
    </section>
  );
};
